import React, { useMemo, useState } from 'react';
import { Form, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faCopy, faCheck, faFilter } from '@fortawesome/free-solid-svg-icons';

/**
 * Lightweight raw JSON inspector (similar to jsoneditoronline.org):
 * - pretty-printed JSON
 * - search/highlight any attribute or value, with match count
 * - optional "show only matching lines" filter
 * - copy the whole JSON to the clipboard
 */
const escapeRegExp = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const HighlightedLine = ({ text, query }) => {
  if (!query) return <span>{text}</span>;

  const regex = new RegExp(`(${escapeRegExp(query)})`, 'gi');
  const parts = text.split(regex);

  return (
    <span>
      {parts.map((part, idx) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <mark key={idx} className="p-0" style={{ backgroundColor: '#ffe066' }}>
            {part}
          </mark>
        ) : (
          <span key={idx}>{part}</span>
        )
      )}
    </span>
  );
};

const RawJsonViewer = ({ data, maxHeight = '60vh' }) => {
  const [query, setQuery] = useState('');
  const [onlyMatches, setOnlyMatches] = useState(false);
  const [copied, setCopied] = useState(false);

  const jsonString = useMemo(() => {
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  }, [data]);

  const lines = useMemo(() => jsonString.split('\n'), [jsonString]);

  const trimmedQuery = query.trim();

  const matchCount = useMemo(() => {
    if (!trimmedQuery) return 0;
    const regex = new RegExp(escapeRegExp(trimmedQuery), 'gi');
    const found = jsonString.match(regex);
    return found ? found.length : 0;
  }, [jsonString, trimmedQuery]);

  const visibleLines = useMemo(() => {
    if (!trimmedQuery || !onlyMatches) {
      return lines.map((text, idx) => ({ text, number: idx + 1 }));
    }
    const lower = trimmedQuery.toLowerCase();
    return lines
      .map((text, idx) => ({ text, number: idx + 1 }))
      .filter((line) => line.text.toLowerCase().includes(lower));
  }, [lines, trimmedQuery, onlyMatches]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(jsonString);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Fallback for older browsers / insecure contexts
      const textarea = document.createElement('textarea');
      textarea.value = jsonString;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      } catch {
        /* noop */
      }
      document.body.removeChild(textarea);
    }
  };

  return (
    <div>
      <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
        <div className="input-group flex-grow-1" style={{ minWidth: '220px' }}>
          <span className="input-group-text">
            <FontAwesomeIcon icon={faSearch} />
          </span>
          <Form.Control
            type="text"
            placeholder="Search attribute or value..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {trimmedQuery && (
            <span className="input-group-text">
              {matchCount} {matchCount === 1 ? 'match' : 'matches'}
            </span>
          )}
        </div>
        <Button
          variant="outline-secondary"
          size="sm"
          active={onlyMatches}
          onClick={() => setOnlyMatches((v) => !v)}
          disabled={!trimmedQuery}
          title="Show only matching lines"
        >
          <FontAwesomeIcon icon={faFilter} className="me-1" />
          Only matches
        </Button>
        <Button variant="outline-primary" size="sm" onClick={handleCopy} title="Copy full JSON">
          <FontAwesomeIcon icon={copied ? faCheck : faCopy} className="me-1" />
          {copied ? 'Copied' : 'Copy JSON'}
        </Button>
      </div>

      <pre
        className="bg-dark text-light p-3 rounded mb-0"
        style={{
          maxHeight,
          overflow: 'auto',
          fontSize: '0.8rem',
          lineHeight: 1.5,
          whiteSpace: 'pre',
        }}
      >
        {visibleLines.length === 0 ? (
          <span className="text-muted">No matching lines</span>
        ) : (
          visibleLines.map((line) => (
            <div key={line.number} style={{ display: 'flex' }}>
              <span
                className="text-muted text-end pe-3"
                style={{ userSelect: 'none', minWidth: '3.5em', flexShrink: 0 }}
              >
                {line.number}
              </span>
              <code style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                <HighlightedLine text={line.text} query={trimmedQuery} />
              </code>
            </div>
          ))
        )}
      </pre>
    </div>
  );
};

export default RawJsonViewer;
